import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { usePublicConfig } from '../context';
import { AuthApi } from '../services';
import { normalizePhoneNumber } from '../utils/phone';
import { ArrowLeft, CheckCircle2, Clock, Smartphone, Lock } from 'lucide-react';

const requestOtpSchema = yup
  .object({
    phone: yup.string().required('Phone number is required'),
  })
  .required();

const resetPasswordSchema = yup
  .object({
    otp: yup
      .string()
      .required('OTP is required')
      .matches(/^\d{6}$/, 'OTP must be 6 digits'),
    new_password: yup
      .string()
      .min(6, 'Password must be at least 6 characters')
      .required('New password is required'),
    confirm_password: yup
      .string()
      .oneOf([yup.ref('new_password')], 'Passwords must match')
      .required('Please confirm your password'),
  })
  .required();

type RequestOtpFormValues = yup.InferType<typeof requestOtpSchema>;
type ResetPasswordFormValues = yup.InferType<typeof resetPasswordSchema>;

export const PasswordResetPage = () => {
  const [step, setStep] = useState<'request' | 'verify'>('request');
  const [phone, setPhone] = useState('');
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [otpSent, setOtpSent] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const navigate = useNavigate();
  const { config: publicConfig } = usePublicConfig();
  const chamaName = publicConfig?.name ?? 'LoopFund';

  const {
    register: registerRequest,
    handleSubmit: handleSubmitRequest,
    formState: { errors: requestErrors, isSubmitting: isRequestSubmitting },
  } = useForm<RequestOtpFormValues>({
    resolver: yupResolver(requestOtpSchema),
    defaultValues: { phone: '' },
  });

  const {
    register: registerReset,
    handleSubmit: handleSubmitReset,
    formState: { errors: resetErrors, isSubmitting: isResetSubmitting },
  } = useForm<ResetPasswordFormValues>({
    resolver: yupResolver(resetPasswordSchema),
    defaultValues: { otp: '', new_password: '', confirm_password: '' },
  });

  const onRequestOtp = async (values: RequestOtpFormValues) => {
    setErrorMessage(null);
    setSuccessMessage(null);

    const normalizedPhone = normalizePhoneNumber(values.phone.trim());

    try {
      const response = await AuthApi.requestPasswordReset({ phone: normalizedPhone });
      setPhone(normalizedPhone);
      setOtpSent(true);
      setStep('verify');
      setSuccessMessage(response.message || 'OTP sent to your phone. Valid for 10 minutes.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to send OTP. Please try again.';
      setErrorMessage(message);
    }
  };

  const onResetPassword = async (values: ResetPasswordFormValues) => {
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await AuthApi.resetPassword({
        phone,
        otp: values.otp,
        new_password: values.new_password,
      });

      setResetSuccess(true);
      setSuccessMessage('Password reset successful! Redirecting to login...');

      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to reset password. Please try again.';
      setErrorMessage(message);
    }
  };

  return (
    <section className="relative flex min-h-screen w-full flex-col bg-slate-900 text-slate-100 lg:flex-row">
      {/* Left side - Branding */}
      <div
        className="fixed inset-x-0 top-0 h-[48vh] w-full overflow-hidden lg:hidden"
        style={{
          backgroundImage: "url('https://i.ibb.co/7xFJnX3B/139539.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/70 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 px-6 pb-[calc(12vh+env(safe-area-inset-bottom))] pt-16">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/60 px-4 py-1 text-xs font-semibold uppercase tracking-[0.35em] text-brand-100">
            {chamaName}
          </span>
          <h1 className="mt-3 font-display text-3xl leading-snug text-white">Reset your password.</h1>
          <p className="mt-2 text-sm leading-relaxed text-brand-100/80">
            Secure your account with a new password in just a few steps.
          </p>
        </div>
      </div>

      <div
        className="relative hidden flex-1 items-center justify-center px-16 py-20 lg:flex"
        style={{
          backgroundImage: "url('https://i.ibb.co/7xFJnX3B/139539.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 bg-slate-900/80" />
        <div className="relative z-10 max-w-md space-y-6">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/60 px-4 py-1 text-xs font-semibold uppercase tracking-[0.35em] text-brand-100">
            {chamaName}
          </span>
          <h1 className="font-display text-4xl leading-tight text-white">Reset your password.</h1>
          <p className="text-base leading-relaxed text-brand-100/70">
            Enter your phone number and we'll send you an OTP code to reset your password securely.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="mt-[48vh] flex flex-1 items-end justify-center bg-transparent pb-8 lg:mt-0 lg:min-h-screen lg:items-center lg:bg-white lg:px-8 lg:py-16 lg:text-slate-900 dark:lg:bg-slate-950 dark:lg:text-slate-100">
        <div className="fixed inset-x-0 bottom-0 z-10 w-full max-h-[80vh] overflow-y-auto rounded-t-[2.5rem] bg-white px-6 py-10 pb-[calc(16px+env(safe-area-inset-bottom))] text-slate-900 shadow-2xl dark:bg-slate-950 dark:text-slate-100 lg:static lg:max-w-md lg:max-h-none lg:overflow-visible lg:rounded-none lg:px-10 lg:py-16 lg:shadow-none">
          <Link
            to="/login"
            className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-600 hover:text-brand-700 dark:text-brand-300"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to login
          </Link>

          <div className="mb-8 text-center lg:text-left">
            <h2 className="font-display text-3xl text-slate-900 dark:text-white">
              {step === 'request' ? 'Reset Password' : 'Verify OTP'}
            </h2>
            <p className="mt-2 text-sm text-slate-500 dark:text-slate-300">
              {step === 'request'
                ? 'Enter your phone number to receive an OTP code'
                : 'Enter the 6-digit code sent to your phone'}
            </p>
          </div>

          {resetSuccess ? (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 dark:border-emerald-500/40 dark:bg-emerald-500/10">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="mt-0.5 h-6 w-6 text-emerald-600 dark:text-emerald-300" />
                <div>
                  <h3 className="font-semibold text-emerald-900 dark:text-emerald-100">Password reset successful!</h3>
                  <p className="mt-2 text-sm text-emerald-700 dark:text-emerald-200">
                    Redirecting you to the login page...
                  </p>
                </div>
              </div>
            </div>
          ) : step === 'request' ? (
            <form onSubmit={handleSubmitRequest(onRequestOtp)} className="space-y-6" noValidate>
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-slate-700 dark:text-slate-200">
                  Phone number
                </label>
                <div className="relative mt-2">
                  <Smartphone className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input
                    id="phone"
                    type="tel"
                    autoComplete="tel"
                    placeholder="0712345678"
                    className="w-full rounded-xl border border-slate-300 bg-white py-3 pl-10 pr-4 text-sm text-slate-900 shadow-sm outline-none transition focus:border-brand-500 focus:ring focus:ring-brand-200/60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-brand-300"
                    {...registerRequest('phone')}
                  />
                </div>
                {requestErrors.phone && <p className="mt-2 text-sm text-red-500">{requestErrors.phone.message}</p>}
              </div>

              <button
                type="submit"
                disabled={isRequestSubmitting}
                className="inline-flex w-full items-center justify-center rounded-full bg-brand-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-brand-400"
              >
                {isRequestSubmitting ? (
                  <>
                    <Clock className="mr-2 h-4 w-4 animate-spin" />
                    Sending OTP...
                  </>
                ) : (
                  'Send OTP'
                )}
              </button>

              {successMessage && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200">
                  {successMessage}
                </div>
              )}

              {errorMessage && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200">
                  {errorMessage}
                </div>
              )}
            </form>
          ) : (
            <form onSubmit={handleSubmitReset(onResetPassword)} className="space-y-6" noValidate>
              {otpSent && (
                <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700 dark:border-blue-500/40 dark:bg-blue-500/10 dark:text-blue-200">
                  <p className="font-semibold">OTP sent to {phone}</p>
                  <p className="mt-1">The code is valid for 10 minutes.</p>
                </div>
              )}

              <div>
                <label htmlFor="otp" className="block text-sm font-semibold text-slate-700 dark:text-slate-200">
                  OTP Code
                </label>
                <input
                  id="otp"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  className="mt-2 w-full rounded-xl border border-slate-300 bg-white px-4 py-3 text-center text-lg font-semibold tracking-widest text-slate-900 shadow-sm outline-none transition focus:border-brand-500 focus:ring focus:ring-brand-200/60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-brand-300"
                  {...registerReset('otp')}
                />
                {resetErrors.otp && <p className="mt-2 text-sm text-red-500">{resetErrors.otp.message}</p>}
              </div>

              <div>
                <label
                  htmlFor="new_password"
                  className="block text-sm font-semibold text-slate-700 dark:text-slate-200"
                >
                  New Password
                </label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input
                    id="new_password"
                    type="password"
                    autoComplete="new-password"
                    className="w-full rounded-xl border border-slate-300 bg-white py-3 pl-10 pr-4 text-sm text-slate-900 shadow-sm outline-none transition focus:border-brand-500 focus:ring focus:ring-brand-200/60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-brand-300"
                    {...registerReset('new_password')}
                  />
                </div>
                {resetErrors.new_password && (
                  <p className="mt-2 text-sm text-red-500">{resetErrors.new_password.message}</p>
                )}
              </div>

              <div>
                <label
                  htmlFor="confirm_password"
                  className="block text-sm font-semibold text-slate-700 dark:text-slate-200"
                >
                  Confirm Password
                </label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" />
                  <input
                    id="confirm_password"
                    type="password"
                    autoComplete="new-password"
                    className="w-full rounded-xl border border-slate-300 bg-white py-3 pl-10 pr-4 text-sm text-slate-900 shadow-sm outline-none transition focus:border-brand-500 focus:ring focus:ring-brand-200/60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:focus:border-brand-300"
                    {...registerReset('confirm_password')}
                  />
                </div>
                {resetErrors.confirm_password && (
                  <p className="mt-2 text-sm text-red-500">{resetErrors.confirm_password.message}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isResetSubmitting}
                className="inline-flex w-full items-center justify-center rounded-full bg-brand-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-brand-400"
              >
                {isResetSubmitting ? 'Resetting password...' : 'Reset Password'}
              </button>

              {successMessage && (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200">
                  {successMessage}
                </div>
              )}

              {errorMessage && (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-200">
                  {errorMessage}
                </div>
              )}

              <button
                type="button"
                onClick={() => {
                  setStep('request');
                  setOtpSent(false);
                  setErrorMessage(null);
                  setSuccessMessage(null);
                }}
                className="w-full text-sm text-slate-600 hover:text-slate-900 dark:text-slate-300 dark:hover:text-slate-100"
              >
                Didn't receive code? Request new OTP
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

